import { useState } from 'react'
import { Check, Copy, Smartphone } from 'lucide-react'
import { toast } from 'sonner'
import { AppModal } from '@/components/common/AppModal'
import { Button } from '@/components/ui/button'
import { formatPakistanPhone } from '@/modules/sales/utils/phone'
import { useCancelCall } from '@/modules/calls/hooks/useCalls'
import { callService } from '@/modules/calls/services/call.service'
import { useCallStore } from '@/modules/calls/store/call.store'
import { getApiErrorToastMessage } from '@/utils/form-errors'

/**
 * Desktop, no browser calling yet: show the number to dial from the agent's
 * phone. Never pretends a call is ringing in the browser.
 */
export function CallDesktopPanel() {
  const desktopCall = useCallStore((s) => s.desktopCall)
  const closeDesktop = useCallStore((s) => s.closeDesktop)
  const openOutcome = useCallStore((s) => s.openOutcome)
  const cancel = useCancelCall()
  const [copied, setCopied] = useState(false)

  if (!desktopCall) return null
  const { call, number, leadName } = desktopCall

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(number)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
      void callService.clientEvent(call.id, 'number_copied').catch(() => undefined)
    } catch {
      toast.error('Could not copy. Select the number instead')
    }
  }

  const onDidNotCall = async () => {
    try {
      await cancel.mutateAsync(call.id)
      closeDesktop()
    } catch (err) {
      toast.error(getApiErrorToastMessage(err, 'Could not cancel call'))
    }
  }

  return (
    <AppModal
      open
      // Closing keeps the call open — the banner brings the result form back.
      onOpenChange={(open) => (open ? undefined : closeDesktop())}
      title="Call from your phone"
      description={leadName}
      moduleId="sales"
      size="sm"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-3">
          <Smartphone className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          <span className="min-w-0 flex-1 select-all font-mono text-lg font-semibold tracking-wide">
            {formatPakistanPhone(number) ?? number}
          </span>
          <Button type="button" variant="outline" size="sm" className="h-8 shrink-0 gap-1.5" onClick={onCopy}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-border/60 pt-3 sm:flex-row sm:justify-between">
          <Button type="button" variant="ghost" className="h-9" onClick={onDidNotCall} disabled={cancel.isPending}>
            Didn't call
          </Button>
          <Button type="button" className="h-9" onClick={() => openOutcome(call.id)}>
            Save result
          </Button>
        </div>
      </div>
    </AppModal>
  )
}
